import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/browser'
import { PostHogProvider } from 'posthog-js/react'
import { QueryClientProvider } from '@tanstack/react-query'
import 'dayjs/locale/fr'
import 'dayjs/locale/es'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/de'
import 'dayjs/locale/it'
import 'dayjs/locale/pl'
import 'dayjs/locale/tr'
import 'dayjs/locale/sv'
import NextApp from './NextApp'
import { NotificationsProvider } from './context/notificationsContext/store'
import { ThemeProvider } from './context/themeContext'
import { MenuProvider } from './context/menuContext'
import { store } from './store/configure'
import { queryClient } from './services/tanstack-query'

import './index.css'
import { PostHogConfig } from 'posthog-js'


if (process.env.REACT_APP_ENVIRONMENT === 'production' &&  process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    release: process.env.REACT_APP_SENTRY_VERSION,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      const exceptionValues = event.exception?.values
      if (exceptionValues?.some(exc =>
        exc.value === 'no_tokens' ||
        exc.value === 'Error: no_tokens' ||
        exc.value === 'No access token' ||
        exc.value === 'Request aborted' ||
        exc.value === 'ID and Access tokens are not available'
      )) {
        return null
      }
      return event
    },
  })
}

const options: Partial<PostHogConfig> = {
  ui_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  api_host: 'https://ph.vacationtracker.io', // We use proxy
  disable_session_recording: true,
  person_profiles: 'identified_only',
  autocapture: false,
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
    captureCanvas: {
      recordCanvas: true,
    },
  },
}

const posthogApiKey: string = process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ''

const app = (
  <QueryClientProvider client={queryClient}>
    <PostHogProvider
      apiKey={posthogApiKey}
      options={options}
    >
      <NotificationsProvider>
        <ThemeProvider>
          <MenuProvider>
            <Provider store={store}>
              <NextApp />
            </Provider>
          </MenuProvider>
        </ThemeProvider>
      </NotificationsProvider>
    </PostHogProvider>
  </QueryClientProvider>
)


const container = document.getElementById('root')

if (container) {
  const root = createRoot(container)
  root.render(process.env.REACT_ENABLE_STRICT_MODE ? <React.StrictMode>{app}</React.StrictMode> : app)
}

